import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { PointSummaryDTO } from 'src/app/shared/models/dtos/responses/point-summary/point-summary-dto';
import { SysadminMonthPointBalanceResponseDTO } from 'src/app/shared/models/dtos/responses/sysadmin-month-point-balance-response-dto/sysadmin-month-point-balance-response-dto';
import { FilterOptions } from 'src/app/shared/models/filters/filter-options';
import { TimelineComponent } from 'src/app/shared/components/organisms/timeline/timeline.component';
import { ProductService } from 'src/app/core/api/product/product.service';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family-dto/family-dto';
import {
  generateSeriesChips,
  MultiSelectChip,
} from 'src/app/shared/components/organisms/filter-container/filter-container.component';
import { RewardService } from 'src/app/core/api/reward/reward.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { forkJoin } from 'rxjs';
import { BusinessStatus } from 'src/app/shared/models/constants/business-status';

@Component({
  selector: 'app-customer-points',
  templateUrl: './customer-points.component.html',
  styleUrls: ['./customer-points.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

/**
 * Customer points from customer details page
 */
export class CustomerPointsComponent implements OnInit {
  @ViewChild(TimelineComponent) timeline!: TimelineComponent;
  @Input() businessId?: number;
  data?: {
    pointSummary: PointSummaryDTO;
    businessInfo: BusinessSummaryResponseDTO;
    sysAdminPointBalance: SysadminMonthPointBalanceResponseDTO;
  };
  addedPointsTotal: number = 0;
  expiringPointsTotal: number = 0;

  @Input() viewOnly!: boolean;
  @Input() businessSummary?: BusinessSummaryResponseDTO;

  businessDisabled: boolean = false;

  chipContents: ChipContent[] = [
    ChipContent.PointsAdded(),
    ChipContent.PointsRedeemed(),
    ChipContent.PointsExpired(),
  ];

  allFilters: FilterOptions = {
    added: false,
    redeemed: false,
    removed: false,
    spend: false,
    expired: false,
    dateRange: { startDate: undefined, endDate: undefined },
    series: [],
  };

  labels = {
    generateReport: 'generate-report',
  };

  filters: FilterOptions = {
    added: false,
    redeemed: false,
    removed: false,
    spend: false,
    expired: false,
    dateRange: { startDate: undefined, endDate: undefined },
  };

  seriesChips!: MultiSelectChip[];
  allFamiliesWithEquipments: any;

  seriesFilter: FilterOptions = {
    series: [],
  };

  loading: boolean = true;
  language = '';

  @Output() attPoints = new EventEmitter<number>();
  @Output() selectedFilters = new EventEmitter<any>();

  /**
   * @constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    public dialog: MatDialog,
    private dialogService: DialogService,
    public readonly productService: ProductService,
    private cdr: ChangeDetectorRef,
    private readonly rewardService: RewardService,
    private readonly userService: UserService
  ) {
    this.language = this.translationService.getActiveLang();
  }

  /**
   * on init
   */
  ngOnInit(): void {
    this.translationService.translateObject(this.labels);
    this.getPrintersFamily();
  }

  /**
   * Opens Create a report modal
   */
  openCreateReportModal() {
    this.dialogService.openCreateReportModal({
      businessId: this.businessId!,
      family: this.getSelectedFamily(),
    });
  }

  /**
   * Opens a create point transaction dialog
   */
  async openCreatePointTransaction() {
    this.dialogService.openTransactionPointModal(
      {
        totalPoints: this.data?.pointSummary?.totalPoints,
        businessId: this.businessId,
        family: this.getSelectedFamily(),
        pointBalance: this.data?.sysAdminPointBalance,
      },
      this.reloadData
    );
  }
  /**
   * Checks is business is disabled
   * @return {boolean}
   */
  isDisabled(): boolean {
    return this.data?.businessInfo?.status === BusinessStatus.DISABLED;
  }

  /**
   * Captures all the families from registered equipment
   */
  getPrintersFamily() {
    this.productService
      .getBusinessEquipments(this.businessId!)
      .subscribe((equipments) => {
        const families = equipments
          .map((e) => e.familyResponseDTO)
          .filter((f): f is FamilyDTO => !!f);
        this.allFamiliesWithEquipments = new Map(
          families!.map((f) => [f.id, f])
        );

        const allFamilies = Array.from(
          new Map(
            this.businessSummary!.businessLevels!.map((f) => [
              f.id,
              {
                id: f.familyPk,
                name: f.familyName,
              },
            ])
          ).values()
        );
        this.seriesChips = generateSeriesChips(allFamilies);
        if (this.seriesChips.length > 0) {
          this.seriesChips[0].isActive = true;
          const initialSeriesFilter: FilterOptions = {
            series: [this.seriesChips[0].id],
          };
          this.updateFilter(initialSeriesFilter);
        } else {
          this.loading = false;
        }
      });
  }

  /**
   * @param {FamilyDTO} family region to extract name from
   * @return {string} extracted country name
   */
  familyName(family: FamilyDTO): string {
    return family.name;
  }

  /**
   * @return {boolean} indicates if the timeline is disabled
   */
  isTimelineDisabled(): boolean {
    const familySelected = this.getSelectedFamily();
    if (
      familySelected &&
      this.allFamiliesWithEquipments.has(familySelected.id)
    ) {
      return false;
    }
    return true;
  }

  /**
   * Get point summary values
   */
  getPointSummary() {
    forkJoin({
      pointSummary: this.rewardService.getBusinessPointSummaryByFamily(
        this.businessId!,
        this.getSelectedFamily().id
      ),
      businessInfo: this.userService.getBusinessSummary(this.businessId!),
      sysAdminBalance: this.rewardService.getSysadminMonthPointBalance(
        this.businessId!,
        this.getSelectedFamily().id
      ),
    }).subscribe({
      next: (res) => {
        this.data = {
          pointSummary: res.pointSummary,
          businessInfo: res.businessInfo,
          sysAdminPointBalance: res.sysAdminBalance,
        };
        this.loading = false;
      },
    });
  }

  /**
   * Updates Filters
   * @param {FilterOptions} filters
   */
  updateFilter(filters: FilterOptions) {
    const merged = { ...this.allFilters, ...filters };
    this.allFilters = { ...this.setFilters(merged) };

    this.filters = {
      added: this.allFilters.added,
      redeemed: this.allFilters.redeemed,
      removed: this.allFilters.removed,
      expired: this.allFilters.expired,
      dateRange: this.allFilters.dateRange,
    };
    this.seriesFilter = {
      series: this.allFilters.series,
    };
    if (this.seriesFilter.series && this.seriesFilter.series.length > 0) {
      this.getPointSummary();
    }
  }

  /**
   * Sets values for filters
   * @param {FilterOptions} filters
   * @return {FilterOptions}
   */
  setFilters(filters: FilterOptions): FilterOptions {
    const updated = { ...filters };

    updated.spend = updated.redeemed;
    updated.removed = updated.redeemed;

    if (updated.dateRange) {
      Object.assign(updated, updated.dateRange);
    }

    return updated;
  }

  /**
    const selectedFamily = this.getSelectedFamily();
        family: selectedFamily,
   * Opens a create point transaction dialog
   * @return {any}
   */
  getSelectedFamily(): any {
    return this.seriesChips.find(
      (chip) => chip.id === this.allFilters.series?.[0]
    );
  }

  /**
   * Reloads transaction points after modal is closed with a successful update
   * @param {any} data
   */
  reloadData = (data: any) => {
    if (data && data !== MatDialogResponse.FAIL) {
      this.data!.pointSummary!.totalPoints = data.totalPoints;
      if (data.operation === 'ADD') {
        this.data!.pointSummary!.addedOnThisMonth =
          (this.data!.pointSummary!.addedOnThisMonth as number) +
          Number(data.inputValue);
      }
      this.updateFilter(this.allFilters);
    }
  };

  /**
   * get added points from the timeline
   * @param {number} points
   */
  handleAddedPoints(points: number) {
    this.addedPointsTotal = points;
    this.cdr.detectChanges();
  }

  /**
   * get expired points from the timeline
   * @param {number} points
   */
  handleExpiringPoints(points: number) {
    this.expiringPointsTotal = points;
    this.cdr.detectChanges();
  }
}
